import { generatedCategories } from './symbols/generated';

export interface SymbolEntry {
    name: string;
    code: string;
    /** Languages this character belongs to (only used by the 'language' category). */
    languages?: string[];
}

export interface SymbolCategory {
    id: string;
    label: string;
    symbols: SymbolEntry[];
}

/** Derive the literal character from a "U+XXXX" code-point string. */
export const glyph = (code: string): string => String.fromCodePoint(parseInt(code.slice(2), 16));

export const symbolCategories: SymbolCategory[] = [
    {
        id: 'maths',
        label: 'Maths',
        symbols: [
            { name: 'Degree', code: 'U+00B0' },
            { name: 'Times', code: 'U+00D7' },
            { name: 'Divide', code: 'U+00F7' },
            { name: 'Plus-minus', code: 'U+00B1' },
            { name: 'Not equal', code: 'U+2260' },
            { name: 'Approximately', code: 'U+2248' },
            { name: 'Less or equal', code: 'U+2264' },
            { name: 'Greater or equal', code: 'U+2265' },
            { name: 'Square root', code: 'U+221A' },
            { name: 'Cube root', code: 'U+221B' },
            { name: 'Fourth root', code: 'U+221C' },
            { name: 'Pi', code: 'U+03C0' },
            { name: 'Infinity', code: 'U+221E' },
            { name: 'Summation', code: 'U+2211' },
            { name: 'Product', code: 'U+220F' },
            { name: 'Partial deriv.', code: 'U+2202' },
            { name: 'Integral', code: 'U+222B' },
            { name: 'Therefore', code: 'U+2234' },
            { name: 'Because', code: 'U+2235' },
            { name: 'Proportional', code: 'U+221D' },
        ],
    },
    {
        id: 'currency',
        label: 'Currency',
        symbols: [
            { name: 'Pound', code: 'U+00A3' },
            { name: 'Dollar', code: 'U+0024' },
            { name: 'Euro', code: 'U+20AC' },
            { name: 'Yen', code: 'U+00A5' },
            { name: 'Cent', code: 'U+00A2' },
            { name: 'Rupee', code: 'U+20B9' },
            { name: 'Won', code: 'U+20A9' },
            { name: 'Bitcoin', code: 'U+20BF' },
            { name: 'Ruble', code: 'U+20BD' },
            { name: 'Turkish Lira', code: 'U+20BA' },
            { name: 'Hryvnia', code: 'U+20B4' },
            { name: 'Shekel', code: 'U+20AA' },
            { name: 'Baht', code: 'U+0E3F' },
            { name: 'Dong', code: 'U+20AB' },
            { name: 'Peso', code: 'U+20B1' },
            { name: 'Naira', code: 'U+20A6' },
            { name: 'Lari', code: 'U+20BE' },
            { name: 'Tenge', code: 'U+20B8' },
            { name: 'Taka', code: 'U+09F3' },
            { name: 'Currency sign', code: 'U+00A4' },
        ],
    },
    {
        id: 'geometry',
        label: 'Geometry',
        symbols: [
            { name: 'Angle', code: 'U+2220' },
            { name: 'Right angle', code: 'U+221F' },
            { name: 'Perpendicular', code: 'U+22A5' },
            { name: 'Parallel', code: 'U+2225' },
            { name: 'Congruent', code: 'U+2245' },
            { name: 'Similar', code: 'U+007E' },
            { name: 'Triangle', code: 'U+25B3' },
            { name: 'Circle', code: 'U+25CB' },
            { name: 'Pi', code: 'U+03C0' },
        ],
    },
    {
        id: 'fractions',
        label: 'Fractions',
        symbols: [
            { name: 'One half', code: 'U+00BD' },
            { name: 'One third', code: 'U+2153' },
            { name: 'Two thirds', code: 'U+2154' },
            { name: 'One quarter', code: 'U+00BC' },
            { name: 'Three quarters', code: 'U+00BE' },
            { name: 'One fifth', code: 'U+2155' },
            { name: 'Two fifths', code: 'U+2156' },
            { name: 'Three fifths', code: 'U+2157' },
            { name: 'Four fifths', code: 'U+2158' },
            { name: 'One sixth', code: 'U+2159' },
            { name: 'One eighth', code: 'U+215B' },
        ],
    },
    {
        id: 'superscript',
        label: 'Superscript & subscript',
        symbols: [
            { name: 'Superscript 1', code: 'U+00B9' },
            { name: 'Superscript 2', code: 'U+00B2' },
            { name: 'Superscript 3', code: 'U+00B3' },
            { name: 'Superscript 4', code: 'U+2074' },
            { name: 'Superscript 5', code: 'U+2075' },
            { name: 'Superscript 6', code: 'U+2076' },
            { name: 'Superscript minus', code: 'U+207B' },
            { name: 'Superscript plus', code: 'U+207A' },
            { name: 'Subscript 0', code: 'U+2080' },
            { name: 'Subscript 1', code: 'U+2081' },
            { name: 'Subscript 2', code: 'U+2082' },
            { name: 'Subscript 3', code: 'U+2083' },
        ],
    },
    {
        id: 'sets',
        label: 'Sets & logic',
        symbols: [
            { name: 'Element of', code: 'U+2208' },
            { name: 'Not element of', code: 'U+2209' },
            { name: 'Subset of', code: 'U+2282' },
            { name: 'Subset or equal', code: 'U+2286' },
            { name: 'Union', code: 'U+222A' },
            { name: 'Intersection', code: 'U+2229' },
            { name: 'Empty set', code: 'U+2205' },
            { name: 'For all', code: 'U+2200' },
            { name: 'There exists', code: 'U+2203' },
            { name: 'Logical NOT', code: 'U+00AC' },
            { name: 'Logical AND', code: 'U+2227' },
            { name: 'Logical OR', code: 'U+2228' },
        ],
    },

    ...generatedCategories,
];

/** Ordered list of languages in the 'language' category, derived from the data. */
export const supportedLanguages: string[] = (() => {
    const present = new Set(
        (symbolCategories.find((c) => c.id === 'language')?.symbols ?? []).flatMap(
            (s) => s.languages ?? [],
        ),
    );
    return [...present].sort();
})();
